import { useEffect, useRef, useState } from "react";
import { Alert, Badge, Button, Table } from "flowbite-react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import {
  HiDocumentText,
  HiFilm,
  HiInformationCircle,
  HiPaperClip,
  HiPhotograph,
  HiSpeakerphone,
  HiUser,
  HiUserGroup,
  HiX,
} from "react-icons/hi";
import { IoMdSearch } from "react-icons/io";
import { MdAdd, MdOutlineDrafts, MdOutlineMarkEmailRead } from "react-icons/md";
import { FiEye } from "react-icons/fi";

import { ANNOUNCEMENTS } from "../../config/apiConfig";
import axios from "../../config/axiosInstance";
import Modal from "../modals/Modal";
import useDebouncedValue from "../../hooks/useDebouncedValue";
import {
  formatDateTime,
  formatNumber,
  getErrorMessage,
} from "../customers/customerHelpers";
import { SkeletonWorkList } from "../crm/Skeletons";
import ClientPicker from "../crm/ClientPicker";

const MAX_TITLE = 80;
const MAX_MESSAGE = 500;

/*
  Anexos: imagem, PDF e vídeo. Estes limites espelham os do servidor
  (appointments-backend/config/s3.js) só para avisar ANTES de subir um vídeo de
  50MB: quem manda de verdade é o servidor, que confere o tipo pelo conteúdo do
  arquivo e devolve a mensagem em português se algo passar do limite dele.
*/
const MB = 1024 * 1024;
const MAX_ATTACHMENTS = 5;
const MAX_ATTACHMENTS_TOTAL = 60 * MB;
const ATTACHMENT_LIMITS = { IMAGE: 10 * MB, PDF: 10 * MB, VIDEO: 50 * MB };
const ATTACHMENT_LABELS = { IMAGE: "imagem", PDF: "PDF", VIDEO: "vídeo" };
const ATTACHMENT_TYPES = {
  "image/jpeg": "IMAGE",
  "image/png": "IMAGE",
  "image/webp": "IMAGE",
  "image/gif": "IMAGE",
  "application/pdf": "PDF",
  "video/mp4": "VIDEO",
  "video/quicktime": "VIDEO",
  "video/webm": "VIDEO",
};
const ATTACHMENT_EXTENSIONS = {
  jpg: "IMAGE",
  jpeg: "IMAGE",
  png: "IMAGE",
  webp: "IMAGE",
  gif: "IMAGE",
  pdf: "PDF",
  mp4: "VIDEO",
  mov: "VIDEO",
  webm: "VIDEO",
};
const ATTACHMENT_ICONS = {
  IMAGE: HiPhotograph,
  PDF: HiDocumentText,
  VIDEO: HiFilm,
};

// Alguns sistemas não informam o tipo do arquivo; a extensão cobre esse caso.
// O servidor não confia em nenhum dos dois e olha o conteúdo.
const attachmentKindOf = (file) =>
  ATTACHMENT_TYPES[file.type] ||
  ATTACHMENT_EXTENSIONS[String(file.name).split(".").pop().toLowerCase()] ||
  null;

const formatFileSize = (bytes) => {
  if (bytes >= MB) return `${(bytes / MB).toFixed(1).replace(".", ",")} MB`;
  return `${Math.max(1, Math.round(bytes / 1024))} KB`;
};

const sameFile = (first, second) =>
  first.name === second.name &&
  first.size === second.size &&
  first.lastModified === second.lastModified;

const fieldClass =
  "block w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 transition placeholder:text-slate-400 focus:border-pink-500 focus:outline-none focus:ring-0";

const AUDIENCE_ALL = "ALL";
const AUDIENCE_COMPANY = "COMPANY";
// Escolhidos: o servidor responde (e o comunicado fica marcado) assim quando o
// aviso é só para ALGUNS usuários da cliente.
const AUDIENCE_USERS = "USERS";

// Dentro de uma cliente: todos os usuários dela ou só os marcados.
const RECIPIENTS_ALL = "ALL";
const RECIPIENTS_SELECTED = "SELECTED";

const PERMISSION_LABELS = {
  ADMIN: "Administrador",
  POWER_USER: "Power user",
  USER_CALENDARS: "Vê agendas",
  USER: "Comum",
};

const MAX_NAMES_SHOWN = 8;

const namesSummary = (names) =>
  names.length > MAX_NAMES_SHOWN
    ? `${names.slice(0, MAX_NAMES_SHOWN).join(", ")} e mais ${names.length - MAX_NAMES_SHOWN}`
    : names.join(", ");

/**
 * Prévia do público no servidor. Sem cliente é a do geral; com cliente é a dela;
 * com `professionalIds` (mesmo vazio) são só esses usuários dela. Quem chama
 * confere `scope`, `companyId` e `users` da resposta antes de confiar: um servidor
 * antigo ignora o filtro e devolveria um público maior.
 */
const fetchRecipients = async (companyId, professionalIds) => {
  const params = new URLSearchParams();

  if (companyId) params.set("companyId", companyId);
  // Lista vazia vai como `professionalIds=` e o servidor recusa: nunca some do
  // pedido (sumir seria pedir o salão inteiro).
  if (professionalIds !== undefined) {
    params.set("professionalIds", professionalIds.join(","));
  }

  const query = params.toString();
  const { data: response } = await axios.get(
    `${ANNOUNCEMENTS.RECIPIENTS}${query ? `?${query}` : ""}`
  );

  return response.data;
};

const isConfirmedFor = (preview, companyId) =>
  Boolean(preview) &&
  preview.scope === AUDIENCE_COMPANY &&
  Number(preview.companyId) === Number(companyId);

const sameIds = (first, second) =>
  first.length === second.length && first.every((id) => second.includes(id));

const isConfirmedUsers = (preview, companyId, userIds) =>
  Boolean(preview) &&
  preview.scope === AUDIENCE_USERS &&
  Number(preview.companyId) === Number(companyId) &&
  Array.isArray(preview.users) &&
  sameIds(
    preview.users.map((user) => Number(user.id)),
    userIds.map(Number)
  );

/** Miniatura da imagem escolhida; libera a URL temporária ao sair da tela. */
const ImageThumb = ({ file }) => {
  const [url, setUrl] = useState(null);

  useEffect(() => {
    const objectUrl = URL.createObjectURL(file);
    setUrl(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [file]);

  return url ? (
    <img src={url} alt="" className="h-10 w-10 shrink-0 rounded-lg object-cover" />
  ) : (
    <span className="h-10 w-10 shrink-0 rounded-lg bg-slate-100" />
  );
};

/**
 * Escolha dos arquivos do comunicado. A tela confere tipo, tamanho e quantidade
 * ao escolher e explica o que ficou de fora, em vez de deixar o servidor recusar
 * depois de um vídeo inteiro subir.
 */
const AttachmentPicker = ({ files, onChange, disabled }) => {
  const inputRef = useRef(null);
  const [problems, setProblems] = useState([]);
  const total = files.reduce((sum, file) => sum + file.size, 0);

  const addFiles = (picked) => {
    const accepted = [...files];
    let acceptedTotal = total;
    const refused = [];

    picked.forEach((file) => {
      const kind = attachmentKindOf(file);

      if (accepted.some((item) => sameFile(item, file))) return;

      if (!kind) {
        const extension = String(file.name).split(".").pop().toLowerCase();

        refused.push(
          ["heic", "heif"].includes(extension)
            ? `"${file.name}" é uma foto em HEIC (iPhone), formato que não é aceito. Converta para JPG ou PNG e tente de novo.`
            : `"${file.name}" não é um tipo aceito${
                extension && extension !== String(file.name).toLowerCase()
                  ? ` (.${extension})`
                  : ""
              }. Use imagem (JPG, PNG, WebP ou GIF), PDF ou vídeo (MP4, MOV ou WebM).`
        );
      } else if (!file.size) {
        refused.push(`"${file.name}" está vazio.`);
      } else if (file.size > ATTACHMENT_LIMITS[kind]) {
        refused.push(
          `"${file.name}" passa de ${ATTACHMENT_LIMITS[kind] / MB} MB, o limite para ${ATTACHMENT_LABELS[kind]}.`
        );
      } else if (accepted.length >= MAX_ATTACHMENTS) {
        refused.push(
          `"${file.name}" ficou de fora: o máximo é ${MAX_ATTACHMENTS} arquivos por comunicado.`
        );
      } else if (acceptedTotal + file.size > MAX_ATTACHMENTS_TOTAL) {
        refused.push(
          `"${file.name}" ficou de fora: os arquivos juntos passam de ${MAX_ATTACHMENTS_TOTAL / MB} MB.`
        );
      } else {
        accepted.push(file);
        acceptedTotal += file.size;
      }
    });

    setProblems(refused);
    onChange(accepted);
  };

  const handlePick = (event) => {
    addFiles(Array.from(event.target.files || []));
    // Sem limpar, escolher o mesmo arquivo de novo (depois de remover) não dispara.
    event.target.value = "";
  };

  const removeFile = (index) => {
    setProblems([]);
    onChange(files.filter((_, position) => position !== index));
  };

  return (
    <div>
      <div className="mb-1 flex items-center justify-between">
        <span className="text-xs font-medium text-slate-600">
          Anexos <span className="font-normal text-slate-400">(opcional)</span>
        </span>
        <span className="text-xs text-slate-400">
          {files.length}/{MAX_ATTACHMENTS}
        </span>
      </div>

      <input
        ref={inputRef}
        type="file"
        multiple
        className="hidden"
        onChange={handlePick}
        disabled={disabled}
      />

      {files.length ? (
        <ul className="mb-2 space-y-1.5">
          {files.map((file, index) => {
            const kind = attachmentKindOf(file);
            const Icon = ATTACHMENT_ICONS[kind] || HiPaperClip;

            return (
              <li
                key={`${file.name}-${file.size}-${file.lastModified}`}
                className="flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-2 py-1.5"
              >
                {kind === "IMAGE" ? (
                  <ImageThumb file={file} />
                ) : (
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-slate-100 text-slate-500">
                    <Icon size={20} />
                  </span>
                )}
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-medium text-slate-900">
                    {file.name}
                  </span>
                  <span className="block text-xs text-slate-500">
                    {ATTACHMENT_LABELS[kind]} · {formatFileSize(file.size)}
                  </span>
                </span>
                <button
                  type="button"
                  disabled={disabled}
                  onClick={() => removeFile(index)}
                  aria-label={`Remover ${file.name}`}
                  title="Remover"
                  className="shrink-0 rounded-full p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700 disabled:opacity-50"
                >
                  <HiX size={16} />
                </button>
              </li>
            );
          })}
        </ul>
      ) : null}

      <Button
        type="button"
        color="light"
        size="xs"
        disabled={disabled || files.length >= MAX_ATTACHMENTS}
        onClick={() => inputRef.current?.click()}
      >
        <HiPaperClip className="mr-2 h-4 w-4" />
        {files.length ? "Adicionar outro arquivo" : "Anexar arquivos"}
      </Button>
      <p className="mt-1 text-xs text-slate-500">
        Imagem, PDF ou vídeo, até {MAX_ATTACHMENTS} arquivos. Imagem e PDF até{" "}
        {ATTACHMENT_LIMITS.IMAGE / MB} MB, vídeo até {ATTACHMENT_LIMITS.VIDEO / MB}{" "}
        MB (prefira MP4). Quem recebe vê os arquivos no aviso dentro do app; o
        push leva só o texto.
      </p>

      {problems.length ? (
        <div
          role="alert"
          className="mt-2 space-y-1 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700"
        >
          <p className="font-semibold">
            {problems.length === 1
              ? "Este arquivo não foi adicionado:"
              : "Estes arquivos não foram adicionados:"}
          </p>
          {problems.map((problem) => (
            <p key={problem}>{problem}</p>
          ))}
        </div>
      ) : null}
    </div>
  );
};

/** Arquivos de um comunicado já enviado, como a cliente os vê. */
const SentAttachments = ({ attachments }) => {
  if (!attachments?.length) return null;

  return (
    <div className="mt-3 space-y-2 border-t border-slate-100 pt-3">
      <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">
        Anexos ({attachments.length})
      </p>
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        {attachments.map((attachment) => {
          const Icon = ATTACHMENT_ICONS[attachment.kind] || HiPaperClip;

          if (attachment.kind === "VIDEO") {
            return (
              <div key={attachment.id} className="sm:col-span-2">
                {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
                <video
                  controls
                  preload="metadata"
                  playsInline
                  src={attachment.url}
                  className="max-h-64 w-full rounded-xl bg-black"
                />
                <p className="mt-1 truncate text-xs text-slate-500">
                  {attachment.name} · {formatFileSize(attachment.size)}
                </p>
              </div>
            );
          }

          return (
            <a
              key={attachment.id}
              href={attachment.url}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-2 py-1.5 hover:border-pink-300"
            >
              {attachment.kind === "IMAGE" ? (
                <img
                  src={attachment.url}
                  alt=""
                  className="h-12 w-12 shrink-0 rounded-lg object-cover"
                />
              ) : (
                <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-slate-100 text-slate-500">
                  <Icon size={22} />
                </span>
              )}
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-medium text-slate-900">
                  {attachment.name}
                </span>
                <span className="block text-xs text-slate-500">
                  {ATTACHMENT_LABELS[attachment.kind]} ·{" "}
                  {formatFileSize(attachment.size)} · abrir em outra aba
                </span>
              </span>
            </a>
          );
        })}
      </div>
    </div>
  );
};

/** Corpo do envio: JSON como sempre, ou multipart quando há arquivos. */
const buildSendBody = (fields, files) => {
  if (!files.length) return fields;

  const form = new FormData();

  Object.entries(fields).forEach(([key, value]) => {
    // Lista de usuários vai como "1,2,3": o servidor aceita esse formato.
    form.append(key, Array.isArray(value) ? value.join(",") : value);
  });
  files.forEach((file) => form.append("files", file));

  return form;
};

/** Para quem o comunicado foi: todas as clientes, uma cliente ou alguns usuários dela. */
const AudienceBadge = ({ announcement }) => {
  const companyName =
    announcement.companyName || `a cliente #${announcement.companyId}`;

  if (announcement.scope === AUDIENCE_USERS) {
    return (
      <Badge color="pink" className="w-fit text-xs">
        {formatNumber(announcement.recipientsCount ?? 0)} usuário(s) de {companyName}
      </Badge>
    );
  }

  return announcement.scope === AUDIENCE_COMPANY ? (
    <Badge color="pink" className="w-fit text-xs">
      Só {companyName}
    </Badge>
  ) : (
    <Badge color="gray" className="w-fit text-xs">
      Todas as clientes
    </Badge>
  );
};

/** Usuários ativos da cliente, para marcar quem recebe. */
const UserChecklist = ({ users, selectedIds, onToggle, onSetAll, disabled }) => (
  <div className="rounded-xl border border-slate-200 bg-white">
    <div className="flex items-center justify-between gap-2 border-b border-slate-100 px-3 py-1.5">
      <span className="text-xs text-slate-500">
        {formatNumber(selectedIds.length)} de {formatNumber(users.length)} marcados
      </span>
      <span className="flex gap-3 text-xs">
        <button
          type="button"
          disabled={disabled}
          onClick={() => onSetAll(users.map((user) => user.id))}
          className="font-medium text-pink-600 hover:underline"
        >
          Marcar todos
        </button>
        <button
          type="button"
          disabled={disabled}
          onClick={() => onSetAll([])}
          className="font-medium text-slate-500 hover:underline"
        >
          Limpar
        </button>
      </span>
    </div>
    <ul className="max-h-56 divide-y divide-slate-100 overflow-auto">
      {users.map((user) => (
        <li key={user.id}>
          <label className="flex cursor-pointer items-center gap-2 px-3 py-2 hover:bg-slate-50">
            <input
              type="checkbox"
              checked={selectedIds.includes(user.id)}
              disabled={disabled}
              onChange={() => onToggle(user.id)}
              className="h-4 w-4 rounded border-slate-300 text-pink-600 focus:ring-pink-500"
            />
            <span className="min-w-0 flex-1">
              <span className="block truncate text-sm font-medium text-slate-900">
                {user.name}
              </span>
              <span className="block truncate text-xs text-slate-500">
                {user.email}
              </span>
            </span>
            <span className="shrink-0 text-[11px] text-slate-500">
              {PERMISSION_LABELS[user.permissionId] || PERMISSION_LABELS.USER}
            </span>
            {!user.hasPush ? (
              <span
                className="shrink-0 rounded-full bg-slate-100 px-2 py-0.5 text-[11px] text-slate-500"
                title="Sem aparelho com push. Vê o aviso dentro do app."
              >
                sem push
              </span>
            ) : null}
          </label>
        </li>
      ))}
    </ul>
  </div>
);

/** Barra de leitura: o número sozinho não diz se 231 é muito ou pouco. */
const ReadBar = ({ reads, audience, rate }) => (
  <div className="min-w-[130px]">
    <div className="flex items-baseline justify-between gap-2">
      <span className="text-sm font-semibold text-slate-900">
        {formatNumber(reads)}
        <span className="text-xs font-normal text-slate-500">
          {" "}
          / {formatNumber(audience)}
        </span>
      </span>
      <span className="text-xs font-medium text-slate-600">{rate}%</span>
    </div>
    <div className="mt-1 h-1.5 w-full rounded-full bg-slate-100">
      <div
        className="h-1.5 rounded-full bg-pink-500"
        style={{ width: `${Math.min(rate, 100)}%` }}
      />
    </div>
  </div>
);

const PersonRow = ({ person }) => (
  <div className="flex items-center justify-between gap-3 rounded-xl border border-slate-100 bg-slate-50 px-3 py-2">
    <div className="min-w-0">
      <p className="truncate text-sm font-medium text-slate-900">
        {person.name}
      </p>
      <p className="truncate text-xs text-slate-500">
        {person.companyName} · {person.email}
      </p>
    </div>
    {person.readAt ? (
      <span className="shrink-0 text-xs text-slate-500">
        {formatDateTime(person.readAt)}
      </span>
    ) : null}
  </div>
);

/** Painel de leituras de um comunicado. */
const AudiencePanel = ({ announcementId }) => {
  const [tab, setTab] = useState("readers");
  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearch = useDebouncedValue(searchTerm, 400);

  const { isLoading, data } = useQuery(
    ["announcement-audience", announcementId, debouncedSearch],
    async () => {
      const { data: response } = await axios.get(
        `${ANNOUNCEMENTS.LIST}/${announcementId}/audience?search=${encodeURIComponent(
          debouncedSearch
        )}`
      );

      return response.data;
    },
    { enabled: Boolean(announcementId), keepPreviousData: true }
  );

  const announcement = data?.announcement;
  const counts = data?.counts || {};
  const list = tab === "readers" ? data?.readers || [] : data?.pending || [];

  return (
    <div className="space-y-4">
      {announcement ? (
        <section className="rounded-2xl border border-slate-200 bg-white p-4">
          <p className="whitespace-pre-line text-sm text-slate-700">
            {announcement.message}
          </p>
          <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-slate-100 pt-2 text-xs text-slate-500">
            <AudienceBadge announcement={announcement} />
            <span>Enviado em {formatDateTime(announcement.createdAt)}</span>
          </div>
          <SentAttachments attachments={announcement.attachments} />
        </section>
      ) : null}

      <section className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        {[
          {
            label: "Leram",
            value: counts.totalReads,
            className: "text-emerald-600",
          },
          {
            label: "Ainda não leram",
            value: counts.pending,
            className: "text-amber-600",
          },
          {
            label: "Taxa de leitura",
            value: `${announcement?.readRate ?? 0}%`,
            className: "text-slate-900",
          },
        ].map((card) => (
          <div
            key={card.label}
            className="rounded-2xl border border-slate-200 bg-white p-4"
          >
            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">
              {card.label}
            </p>
            <p className={`text-2xl font-bold ${card.className}`}>
              {typeof card.value === "number"
                ? formatNumber(card.value)
                : card.value}
            </p>
          </div>
        ))}
      </section>

      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex gap-2">
          {[
            {
              key: "readers",
              label: `Leram (${formatNumber(counts.readers)})`,
              icon: MdOutlineMarkEmailRead,
            },
            {
              key: "pending",
              label: `Não leram (${formatNumber(counts.pending)})`,
              icon: MdOutlineDrafts,
            },
          ].map((item) => (
            <button
              key={item.key}
              type="button"
              onClick={() => setTab(item.key)}
              className={`flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium transition ${
                tab === item.key
                  ? "border-pink-500 bg-pink-50 text-pink-600"
                  : "border-slate-200 bg-white text-slate-500 hover:text-slate-800"
              }`}
            >
              <item.icon size={14} />
              {item.label}
            </button>
          ))}
        </div>

        <div className="flex min-w-[220px] flex-1 items-center gap-2 rounded-lg border border-slate-200 px-3 py-1.5 focus-within:border-pink-500">
          <IoMdSearch className="shrink-0 text-slate-400" size={16} />
          <input
            type="text"
            placeholder="Buscar por nome, e-mail ou empresa"
            className="w-full border-0 bg-transparent p-0 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-0"
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
          />
        </div>
      </div>

      {isLoading ? (
        // Esqueleto com a forma da lista: o spinner centralizado não dizia
        // quantas linhas vinham nem reservava a altura delas.
        <SkeletonWorkList rows={5} withHeader={false} />
      ) : list.length ? (
        <div className="space-y-2">
          {list.map((person) => (
            <PersonRow key={person.id} person={person} />
          ))}
        </div>
      ) : (
        <p className="py-10 text-center text-sm text-slate-500">
          {searchTerm
            ? "Ninguém encontrado com esse termo."
            : tab === "readers"
              ? "Ninguém leu este comunicado ainda."
              : "Todos os usuários já leram."}
        </p>
      )}

      {counts.totalReads > counts.readers ? (
        <p className="text-xs text-slate-500">
          {formatNumber(counts.totalReads - counts.readers)} leitura(s) vieram de
          usuários que já saíram da base — contam no total, mas não aparecem na
          lista.
        </p>
      ) : null}
    </div>
  );
};

/**
 * Comunicados enviados e o desempenho de leitura de cada um.
 * A base de comparação é o número de usuários ativos hoje.
 */
const AnnouncementsTable = () => {
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState("");
  const [isComposerOpen, setIsComposerOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [title, setTitle] = useState("");
  const [message, setMessage] = useState("");
  const [feedback, setFeedback] = useState(null);
  // Para quem vai. Começa sem escolha, de propósito: o envio só libera depois
  // de a pessoa dizer "todas" ou escolher uma cliente. Nunca há um padrão que
  // possa mandar para a base inteira por descuido.
  const [audience, setAudience] = useState(null);
  const [company, setCompany] = useState(null);
  // Dentro da cliente: todos os usuários dela (padrão) ou só os marcados. Marcar
  // começa sempre vazio, e o envio só libera com pelo menos um marcado.
  const [recipientMode, setRecipientMode] = useState(RECIPIENTS_ALL);
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [isPreparing, setIsPreparing] = useState(false);
  // Arquivos do comunicado (objetos File) e quanto do envio já subiu (0 a 100).
  const [files, setFiles] = useState([]);
  const [uploadProgress, setUploadProgress] = useState(null);
  // Erro do envio, mostrado DENTRO do modal. O alerta da página fica atrás do
  // modal (borrado), então quem clicava em enviar via "nada acontecer".
  const [composerError, setComposerError] = useState(null);
  const composerErrorRef = useRef(null);

  useEffect(() => {
    if (composerError) {
      composerErrorRef.current?.scrollIntoView({
        block: "nearest",
        behavior: "smooth",
      });
    }
  }, [composerError]);

  const debouncedSearch = useDebouncedValue(searchTerm, 400);

  const resetRecipients = () => {
    setRecipientMode(RECIPIENTS_ALL);
    setSelectedUserIds([]);
  };

  const { isLoading, data } = useQuery(
    ["announcements", debouncedSearch],
    async () => {
      const { data: response } = await axios.get(
        `${ANNOUNCEMENTS.LIST}?search=${encodeURIComponent(debouncedSearch)}`
      );

      return response.data;
    }
  );

  const { data: recipients } = useQuery(
    ["announcement-recipients", "all"],
    () => fetchRecipients()
  );

  // Quem recebe, conforme o SERVIDOR, assim que a cliente é escolhida. É este
  // número, e não o que a tela supõe, que a pessoa confirma antes de enviar.
  const companyId = company?.id;
  const {
    data: companyPreview,
    isLoading: isLoadingPreview,
    error: previewError,
  } = useQuery(
    ["announcement-recipients", "company", companyId],
    () => fetchRecipients(companyId),
    { enabled: audience === AUDIENCE_COMPANY && Boolean(companyId) }
  );

  const announcements = data?.data || [];
  const summary = data?.summary || {};

  /*
    O destino é decidido aqui, pelo modo, e nunca deduzido de "tem cliente ou
    não". Sem modo reconhecido (ou individual sem cliente) o envio é recusado
    antes de qualquer chamada: o caminho de falha nunca é o envio geral.
  */
  const sendMutation = useMutation(
    ({
      mode,
      companyId: targetId,
      professionalIds: userIds,
      title: subject,
      message: body,
      files: attachments = [],
    }) => {
      // Com arquivos o corpo é multipart (o cabeçalho JSON padrão do axios
      // converteria o FormData em JSON e perderia os arquivos); sem arquivos
      // segue JSON, igual a antes.
      const send = (url, fields) =>
        axios.post(url, buildSendBody(fields, attachments), {
          ...(attachments.length
            ? {
                headers: { "Content-Type": "multipart/form-data" },
                onUploadProgress: (event) =>
                  setUploadProgress(
                    event.total
                      ? Math.min(100, Math.round((event.loaded / event.total) * 100))
                      : null
                  ),
              }
            : {}),
        });

      if (mode === AUDIENCE_COMPANY) {
        if (!targetId) {
          throw new Error("Escolha a cliente. Nada foi enviado.");
        }

        return send(
          `${ANNOUNCEMENTS.BROADCAST_COMPANY}/${encodeURIComponent(targetId)}`,
          { title: subject, message: body }
        );
      }

      if (mode === AUDIENCE_USERS) {
        if (!targetId || !Array.isArray(userIds) || !userIds.length) {
          throw new Error("Escolha a cliente e os usuários. Nada foi enviado.");
        }

        return send(
          `${ANNOUNCEMENTS.BROADCAST_COMPANY}/${encodeURIComponent(targetId)}/users`,
          { title: subject, message: body, professionalIds: userIds }
        );
      }

      if (mode === AUDIENCE_ALL) {
        return send(ANNOUNCEMENTS.BROADCAST, {
          title: subject,
          message: body,
          audience: AUDIENCE_ALL,
        });
      }

      throw new Error("Escolha para quem enviar. Nada foi enviado.");
    },
    {
      onSuccess: ({ data: response }) => {
        const result = response?.data;
        const push = result?.push;
        const skipped = push?.skippedSharedDevices || 0;
        const chosenNames = (result?.target?.users || []).map((user) => user.name);
        const destination = result?.target
          ? `usuário(s) de ${result.target.companyName}${
              chosenNames.length ? ` (${namesSummary(chosenNames)})` : ""
            }`
          : "usuário(s) de todas as clientes";

        const sentFiles = result?.attachments?.length || 0;

        setFeedback({
          type: "success",
          message: `Comunicado enviado para ${formatNumber(
            result?.recipients ?? 0
          )} ${destination}${
            sentFiles ? ` com ${formatNumber(sentFiles)} anexo(s)` : ""
          }.${
            push ? ` Push: ${formatNumber(push.successCount)} entregues.` : ""
          }${
            skipped
              ? ` ${formatNumber(skipped)} aparelho(s) ficaram de fora do push por também estarem ligados a outra conta que não recebe este aviso, mas quem recebe vê o aviso dentro do app.`
              : ""
          }`,
        });
        setComposerError(null);
        setTitle("");
        setMessage("");
        setFiles([]);
        setAudience(null);
        setCompany(null);
        resetRecipients();
        setIsComposerOpen(false);
        queryClient.invalidateQueries("announcements");
      },
      onError: (error, variables) => {
        const hasFiles = Boolean(variables?.files?.length);
        const status = error?.response?.status;
        const serverMessage = error?.response?.data?.message;
        let message = getErrorMessage(error);

        const totalSize = formatFileSize(
          (variables?.files || []).reduce((sum, file) => sum + file.size, 0)
        );

        // 413 sem mensagem da API é o nginx cortando um envio grande demais.
        if (hasFiles && status === 413 && !serverMessage) {
          message = `Os arquivos (${totalSize} no total) são grandes demais para o servidor. Nada foi enviado. Reduza o tamanho ou envie menos arquivos.`;
        }

        // Sem resposta: a conexão caiu no meio. Servidor que recusa um envio
        // grande demais pode derrubar a conexão antes de a resposta chegar, então
        // o tamanho entra na mensagem. O servidor também pode ter terminado o
        // envio mesmo assim, e reenviar às cegas duplicaria o aviso.
        if (hasFiles && !error?.response) {
          message = `A conexão caiu durante o envio dos anexos (${totalSize} no total). Pode ter sido o tamanho: tente com arquivos menores. Antes de enviar de novo, confira na lista se o comunicado já saiu.`;
        }

        setComposerError(message);
      },
      onSettled: () => setUploadProgress(null),
    }
  );

  const toggleMutation = useMutation(
    ({ id, isActive }) =>
      axios.put(`${ANNOUNCEMENTS.LIST}/${id}`, { isActive }),
    {
      onSuccess: () => queryClient.invalidateQueries("announcements"),
      onError: (error) =>
        setFeedback({ type: "failure", message: getErrorMessage(error) }),
    }
  );

  const isBusy = sendMutation.isLoading || isPreparing;
  const companyConfirmed = isConfirmedFor(companyPreview, companyId);
  const companyHasUsers = companyConfirmed && companyPreview.total > 0;
  const companyUsers =
    companyConfirmed && Array.isArray(companyPreview.users)
      ? companyPreview.users
      : [];
  const canChooseUsers = companyUsers.length > 0;
  const chosenUsers = companyUsers.filter((user) =>
    selectedUserIds.includes(user.id)
  );
  const usersReady =
    recipientMode === RECIPIENTS_ALL ||
    (chosenUsers.length > 0 && chosenUsers.length === selectedUserIds.length);
  const targetReady =
    audience === AUDIENCE_ALL ||
    (audience === AUDIENCE_COMPANY && companyHasUsers && usersReady);
  const canSubmit = Boolean(title.trim() && message.trim()) && targetReady && !isBusy;

  const openComposer = () => {
    setAudience(null);
    setCompany(null);
    resetRecipients();
    setFiles([]);
    setComposerError(null);
    setIsComposerOpen(true);
  };

  // Trocar de público esquece a cliente: nada de cliente escolhida "por trás"
  // de "todas". Clicar de novo na opção já marcada não mexe em nada.
  const chooseAudience = (value) => {
    if (value === audience) return;
    setAudience(value);
    setCompany(null);
    resetRecipients();
  };

  // Outra cliente, outra lista: os marcados da anterior não valem para a nova.
  const chooseCompany = (value) => {
    setCompany(value);
    resetRecipients();
  };

  const chooseRecipientMode = (value) => {
    if (value === recipientMode) return;
    setRecipientMode(value);
    setSelectedUserIds([]);
  };

  const toggleUser = (userId) =>
    setSelectedUserIds((current) =>
      current.includes(userId)
        ? current.filter((id) => id !== userId)
        : [...current, userId]
    );

  // Erro de quem está no compositor: aparece dentro dele (ver composerError).
  const fail = (text) => setComposerError(text);

  // Salão inteiro. Sem popup: a tela já mostra a cliente, quantos recebem e o
  // botão a nomeia. Mas confere de novo no servidor na hora de enviar e só envia
  // se o público for EXATAMENTE o que a tela mostrava. Resposta que não confirma
  // esta cliente (como a de um servidor antigo) ou lista de usuários que mudou
  // nesse meio tempo cancela o envio.
  const sendToWholeCompany = async (content) => {
    const preview = await fetchRecipients(company.id);

    if (!isConfirmedFor(preview, company.id)) {
      return fail(
        "O servidor não confirmou o envio para uma cliente só. Nada foi enviado. Confira se o servidor está atualizado."
      );
    }

    if (!preview.total) {
      return fail("Esta cliente não tem nenhum usuário ativo. Nada foi enviado.");
    }

    const shownIds = companyUsers.map((user) => Number(user.id));
    const freshIds = Array.isArray(preview.users)
      ? preview.users.map((user) => Number(user.id))
      : null;

    if (!freshIds || !sameIds(freshIds, shownIds)) {
      queryClient.invalidateQueries(["announcement-recipients", "company", company.id]);
      return fail(
        "Os usuários desta cliente mudaram desde que você abriu a tela. Nada foi enviado. Confira a lista e envie de novo."
      );
    }

    return sendMutation.mutate({
      mode: AUDIENCE_COMPANY,
      companyId: preview.companyId,
      ...content,
    });
  };

  // Só os marcados. Também sem popup (a tela diz "Vai só para <nomes>"). A lista
  // que o servidor confirma tem de ser exatamente a marcada (nem mais, nem menos).
  const sendToChosenUsers = async (content) => {
    const wanted = [...selectedUserIds];
    if (!wanted.length) return null;

    const preview = await fetchRecipients(company.id, wanted);

    if (!isConfirmedUsers(preview, company.id, wanted)) {
      return fail(
        "O servidor não confirmou os usuários escolhidos. Nada foi enviado. Confira se o servidor está atualizado."
      );
    }

    return sendMutation.mutate({
      mode: AUDIENCE_USERS,
      companyId: preview.companyId,
      professionalIds: preview.users.map((user) => user.id),
      ...content,
    });
  };

  const handleSend = async (event) => {
    event.preventDefault();
    if (!canSubmit) return;
    setComposerError(null);

    const content = { title: title.trim(), message: message.trim(), files };

    if (audience === AUDIENCE_COMPANY) {
      if (!company?.id) return;

      setIsPreparing(true);
      try {
        if (recipientMode === RECIPIENTS_SELECTED) {
          await sendToChosenUsers(content);
        } else {
          await sendToWholeCompany(content);
        }
      } catch (error) {
        fail(getErrorMessage(error));
      } finally {
        setIsPreparing(false);
      }

      return;
    }

    if (audience === AUDIENCE_ALL) {
      const total = recipients?.total ?? 0;

      if (
        !window.confirm(
          `Enviar este comunicado para TODAS as clientes (${formatNumber(
            total
          )} usuário(s) do sistema)?`
        )
      ) {
        return;
      }

      sendMutation.mutate({ mode: AUDIENCE_ALL, ...content });
    }
  };

  const composerSubtitle =
    audience === AUDIENCE_ALL
      ? recipients
        ? `${formatNumber(recipients.total)} usuários ativos · ${formatNumber(
            recipients.withPush
          )} com push`
        : "Calculando destinatários…"
      : audience === AUDIENCE_COMPANY
        ? companyConfirmed
          ? recipientMode === RECIPIENTS_SELECTED
            ? `${companyPreview.companyName} · ${formatNumber(
                chosenUsers.length
              )} de ${formatNumber(companyPreview.total)} usuários marcados`
            : `${companyPreview.companyName} · ${formatNumber(
                companyPreview.total
              )} usuários ativos · ${formatNumber(companyPreview.withPush)} com push`
          : "Escolha a cliente"
        : "Escolha para quem enviar";

  const companySendLabel = () => {
    if (!company) return "Escolha a cliente";
    if (companyConfirmed) {
      if (!companyPreview.total) return "Sem usuário ativo para receber";
      if (recipientMode === RECIPIENTS_SELECTED) {
        return chosenUsers.length
          ? `Enviar só para ${formatNumber(chosenUsers.length)} usuário(s) de ${
              companyPreview.companyName
            }`
          : "Marque quem recebe";
      }

      return `Enviar só para ${companyPreview.companyName}`;
    }

    return isLoadingPreview ? "Conferindo quem recebe…" : "Envio indisponível";
  };

  // Por que o botão de enviar está desabilitado, em palavras. O rótulo do botão
  // já cobre o público; aqui entram título e mensagem.
  const blockReason = !audience
    ? "Escolha para quem enviar."
    : !title.trim()
      ? "Escreva o título para poder enviar."
      : !message.trim()
        ? "Escreva a mensagem para poder enviar."
        : !targetReady
          ? "Termine de escolher quem recebe para poder enviar."
          : null;

  const uploadingLabel =
    sendMutation.isLoading && files.length
      ? uploadProgress === null || uploadProgress >= 100
        ? "Enviando e processando os anexos…"
        : `Enviando anexos… ${uploadProgress}%`
      : null;

  const sendLabel =
    uploadingLabel ||
    (audience === AUDIENCE_ALL
      ? "Enviar para todas as clientes"
      : audience === AUDIENCE_COMPANY
        ? companySendLabel()
        : "Escolha para quem enviar");

  return (
    <>
      <div className="space-y-4">
        <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.3em] text-slate-400">
                Comunicação
              </p>
              <h2 className="text-xl font-semibold text-slate-900">
                Comunicados
              </h2>
              <p className="text-sm text-slate-500">
                Avisos para todas as clientes ou para uma só, e quem já leu cada um.
              </p>
            </div>
            <Button size="sm" onClick={openComposer}>
              <MdAdd className="mr-2 h-4 w-4" />
              Novo comunicado
            </Button>
          </div>
        </section>

        {feedback ? (
          <Alert
            color={feedback.type}
            icon={HiInformationCircle}
            onDismiss={() => setFeedback(null)}
          >
            {feedback.message}
          </Alert>
        ) : null}

        <section className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
          {[
            { label: "Comunicados", value: summary.total },
            { label: "Ativos", value: summary.active },
            { label: "Leituras totais", value: summary.totalReads },
            {
              label: "Taxa média de leitura",
              value: `${summary.averageReadRate ?? 0}%`,
            },
          ].map((card) => (
            <div
              key={card.label}
              className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm"
            >
              <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">
                {card.label}
              </p>
              <p className="text-2xl font-bold text-slate-900">
                {typeof card.value === "number"
                  ? formatNumber(card.value)
                  : card.value}
              </p>
            </div>
          ))}
        </section>

        <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          <div className="flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 focus-within:border-pink-500">
            <IoMdSearch className="shrink-0 text-slate-400" size={18} />
            <input
              type="text"
              placeholder="Buscar por título ou conteúdo"
              className="w-full border-0 bg-transparent p-0 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-0"
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
            />
          </div>
        </section>

        <section className="overflow-x-auto rounded-2xl border border-slate-200 bg-white shadow-sm">
          <Table hoverable>
            <Table.Head className="bg-slate-50">
              <Table.HeadCell>Comunicado</Table.HeadCell>
              <Table.HeadCell className="hidden md:table-cell">
                Enviado em
              </Table.HeadCell>
              <Table.HeadCell>Leitura</Table.HeadCell>
              <Table.HeadCell className="hidden lg:table-cell">
                Última leitura
              </Table.HeadCell>
              <Table.HeadCell>Status</Table.HeadCell>
              <Table.HeadCell className="text-center">Ações</Table.HeadCell>
            </Table.Head>
            <Table.Body className="divide-y">
              {isLoading ? (
                Array.from({ length: 3 }).map((_, rowIndex) => (
                  <Table.Row key={rowIndex}>
                    {Array.from({ length: 6 }).map((__, cellIndex) => (
                      <Table.Cell key={cellIndex}>
                        <div className="h-3 w-full animate-pulse rounded-full bg-slate-200" />
                      </Table.Cell>
                    ))}
                  </Table.Row>
                ))
              ) : announcements.length ? (
                announcements.map((announcement) => (
                  <Table.Row key={announcement.id} className="bg-white">
                    <Table.Cell className="max-w-[320px]">
                      <button
                        type="button"
                        onClick={() => setSelected(announcement)}
                        className="truncate text-left text-sm font-semibold text-slate-900 hover:text-pink-600"
                      >
                        {announcement.title}
                      </button>
                      <p className="truncate text-xs text-slate-500">
                        {announcement.message}
                      </p>
                      <div className="mt-1 flex flex-wrap items-center gap-1">
                        <AudienceBadge announcement={announcement} />
                        {announcement.attachmentsCount ? (
                          <Badge
                            color="gray"
                            icon={HiPaperClip}
                            className="w-fit text-xs"
                            title="Arquivos anexados"
                          >
                            {formatNumber(announcement.attachmentsCount)} anexo(s)
                          </Badge>
                        ) : null}
                      </div>
                    </Table.Cell>
                    <Table.Cell className="hidden whitespace-nowrap text-xs text-slate-600 md:table-cell">
                      {formatDateTime(announcement.createdAt)}
                    </Table.Cell>
                    <Table.Cell>
                      <ReadBar
                        reads={announcement.reads}
                        audience={announcement.audience}
                        rate={announcement.readRate}
                      />
                    </Table.Cell>
                    <Table.Cell className="hidden whitespace-nowrap text-xs text-slate-600 lg:table-cell">
                      {announcement.lastReadAt
                        ? formatDateTime(announcement.lastReadAt)
                        : "—"}
                    </Table.Cell>
                    <Table.Cell>
                      <Badge
                        color={announcement.isActive ? "success" : "gray"}
                        className="w-fit text-xs"
                      >
                        {announcement.isActive ? "Ativo" : "Arquivado"}
                      </Badge>
                    </Table.Cell>
                    <Table.Cell>
                      <div className="flex items-center justify-center gap-1">
                        <Button
                          size="xs"
                          color="light"
                          onClick={() => setSelected(announcement)}
                          title="Ver quem leu"
                        >
                          <FiEye />
                        </Button>
                        <Button
                          size="xs"
                          color="light"
                          disabled={toggleMutation.isLoading}
                          onClick={() =>
                            toggleMutation.mutate({
                              id: announcement.id,
                              isActive: !announcement.isActive,
                            })
                          }
                          title={
                            announcement.isActive
                              ? "Arquivar (some do app)"
                              : "Reativar"
                          }
                        >
                          {announcement.isActive ? "Arquivar" : "Reativar"}
                        </Button>
                      </div>
                    </Table.Cell>
                  </Table.Row>
                ))
              ) : (
                <Table.Row>
                  <Table.Cell
                    colSpan={6}
                    className="py-10 text-center text-sm text-slate-500"
                  >
                    Nenhum comunicado encontrado.
                  </Table.Cell>
                </Table.Row>
              )}
            </Table.Body>
          </Table>
        </section>
      </div>

      <Modal
        isOpen={Boolean(selected)}
        onClose={() => setSelected(null)}
        size="lg"
        icon={HiSpeakerphone}
        title={selected?.title || "Comunicado"}
        subtitle={
          selected
            ? selected.scope === AUDIENCE_USERS
              ? `${formatNumber(selected.recipientsCount ?? 0)} usuário(s) de ${
                  selected.companyName || `a cliente #${selected.companyId}`
                } · ${formatNumber(selected.reads)} leitura(s) · ${
                  selected.readRate
                }% dos escolhidos`
              : selected.scope === AUDIENCE_COMPANY
                ? `Só ${
                    selected.companyName || `a cliente #${selected.companyId}`
                  } · ${formatNumber(selected.reads)} leitura(s) · ${
                    selected.readRate
                  }% dos usuários dela`
                : `${formatNumber(selected.reads)} leitura(s) · ${selected.readRate}% da base`
            : undefined
        }
        bodyClassName="bg-slate-50/50"
      >
        {selected ? <AudiencePanel announcementId={selected.id} /> : null}
      </Modal>

      <Modal
        isOpen={isComposerOpen}
        onClose={setIsComposerOpen}
        size="md"
        icon={HiSpeakerphone}
        title="Novo comunicado"
        subtitle={composerSubtitle}
        closeOnBackdrop={false}
      >
        <form className="space-y-4" onSubmit={handleSend}>
          <fieldset disabled={isBusy}>
            <legend className="mb-1 text-xs font-medium text-slate-600">
              Para quem
            </legend>
            <div
              role="radiogroup"
              aria-label="Para quem enviar"
              className="grid grid-cols-1 gap-2 sm:grid-cols-2"
            >
              {[
                {
                  key: AUDIENCE_ALL,
                  icon: HiUserGroup,
                  label: "Todas as clientes",
                  hint: recipients
                    ? `${formatNumber(recipients.total)} usuários ativos do sistema`
                    : "Todos os usuários ativos do sistema",
                },
                {
                  key: AUDIENCE_COMPANY,
                  icon: HiUser,
                  label: "Só uma cliente",
                  hint: "Você escolhe a cliente e quem dela recebe",
                },
              ].map((option) => (
                <button
                  key={option.key}
                  type="button"
                  role="radio"
                  aria-checked={audience === option.key}
                  onClick={() => chooseAudience(option.key)}
                  className={`flex items-start gap-2 rounded-xl border px-3 py-2 text-left transition ${
                    audience === option.key
                      ? "border-pink-500 bg-pink-50"
                      : "border-slate-200 bg-white hover:border-slate-300"
                  }`}
                >
                  <option.icon
                    size={18}
                    className={`mt-0.5 shrink-0 ${
                      audience === option.key ? "text-pink-600" : "text-slate-400"
                    }`}
                  />
                  <span className="min-w-0">
                    <span className="block text-sm font-semibold text-slate-900">
                      {option.label}
                    </span>
                    <span className="block text-xs text-slate-500">
                      {option.hint}
                    </span>
                  </span>
                </button>
              ))}
            </div>

            {audience === AUDIENCE_COMPANY ? (
              <div className="mt-3 space-y-2">
                <ClientPicker
                  value={company}
                  onChange={chooseCompany}
                  disabled={isBusy}
                />

                {companyId && isLoadingPreview ? (
                  <p className="text-xs text-slate-500">
                    Conferindo quem recebe…
                  </p>
                ) : null}

                {companyId && previewError ? (
                  <p className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
                    {getErrorMessage(previewError)}
                  </p>
                ) : null}

                {companyId && companyPreview && !companyConfirmed ? (
                  <p className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
                    O servidor não confirmou o envio para uma cliente só. Não é
                    possível enviar. Confira se o servidor está atualizado.
                  </p>
                ) : null}

                {companyConfirmed && !companyPreview.total ? (
                  <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                    Esta cliente não tem nenhum usuário ativo. Não há quem
                    receba o comunicado.
                  </p>
                ) : null}

                {companyConfirmed && companyPreview.total ? (
                  <div className="space-y-2">
                    <div
                      role="radiogroup"
                      aria-label="Quem da cliente recebe"
                      className="grid grid-cols-1 gap-2 sm:grid-cols-2"
                    >
                      {[
                        {
                          key: RECIPIENTS_ALL,
                          label: `Todos os usuários (${formatNumber(companyPreview.total)})`,
                          hint: "Todos os ativos desta cliente",
                          disabled: false,
                        },
                        {
                          key: RECIPIENTS_SELECTED,
                          label: "Só alguns usuários",
                          hint: "Você marca quem recebe",
                          disabled: !canChooseUsers,
                        },
                      ].map((option) => (
                        <button
                          key={option.key}
                          type="button"
                          role="radio"
                          aria-checked={recipientMode === option.key}
                          disabled={option.disabled}
                          onClick={() => chooseRecipientMode(option.key)}
                          className={`rounded-xl border px-3 py-1.5 text-left transition disabled:cursor-not-allowed disabled:opacity-50 ${
                            recipientMode === option.key
                              ? "border-pink-500 bg-pink-50"
                              : "border-slate-200 bg-white hover:border-slate-300"
                          }`}
                        >
                          <span className="block text-sm font-semibold text-slate-900">
                            {option.label}
                          </span>
                          <span className="block text-xs text-slate-500">
                            {option.hint}
                          </span>
                        </button>
                      ))}
                    </div>

                    {recipientMode === RECIPIENTS_SELECTED ? (
                      <UserChecklist
                        users={companyUsers}
                        selectedIds={selectedUserIds}
                        onToggle={toggleUser}
                        onSetAll={setSelectedUserIds}
                        disabled={isBusy}
                      />
                    ) : null}

                    {recipientMode === RECIPIENTS_SELECTED && !chosenUsers.length ? (
                      <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                        Marque pelo menos um usuário para poder enviar.
                      </p>
                    ) : null}

                    {recipientMode === RECIPIENTS_SELECTED && chosenUsers.length ? (
                      <p className="rounded-lg border border-pink-200 bg-pink-50 px-3 py-2 text-xs text-pink-800">
                        Vai só para{" "}
                        <strong>
                          {namesSummary(chosenUsers.map((user) => user.name))}
                        </strong>{" "}
                        ({formatNumber(chosenUsers.length)} de{" "}
                        {formatNumber(companyPreview.total)} usuários de{" "}
                        {companyPreview.companyName}). Nenhuma outra pessoa
                        recebe.
                      </p>
                    ) : null}

                    {recipientMode === RECIPIENTS_ALL ? (
                      <p className="rounded-lg border border-pink-200 bg-pink-50 px-3 py-2 text-xs text-pink-800">
                        Vai para {formatNumber(companyPreview.total)} usuário(s)
                        de <strong>{companyPreview.companyName}</strong>.
                        Nenhuma outra cliente recebe.
                      </p>
                    ) : null}

                    {recipientMode === RECIPIENTS_ALL && companyPreview.sharedDevices ? (
                      <p className="text-xs text-slate-500">
                        {formatNumber(companyPreview.sharedDevices)} aparelho(s)
                        não recebem o push porque também estão ligados a outra
                        cliente. Ela vê o aviso dentro do app do mesmo jeito.
                      </p>
                    ) : null}

                    {recipientMode === RECIPIENTS_SELECTED ? (
                      <p className="text-xs text-slate-500">
                        O push só vai para aparelhos que não são usados por quem
                        não foi marcado. Quem ficar sem push vê o aviso dentro do
                        app do mesmo jeito.
                      </p>
                    ) : null}
                  </div>
                ) : null}
              </div>
            ) : null}
          </fieldset>

          <div>
            <div className="mb-1 flex items-center justify-between">
              <label
                className="text-xs font-medium text-slate-600"
                htmlFor="announcement-title"
              >
                Título
              </label>
              <span className="text-xs text-slate-400">
                {title.length}/{MAX_TITLE}
              </span>
            </div>
            <input
              id="announcement-title"
              type="text"
              className={fieldClass}
              placeholder="Ex.: Nova funcionalidade disponível"
              value={title}
              maxLength={MAX_TITLE}
              onChange={(event) => setTitle(event.target.value)}
              required
            />
          </div>

          <div>
            <div className="mb-1 flex items-center justify-between">
              <label
                className="text-xs font-medium text-slate-600"
                htmlFor="announcement-message"
              >
                Mensagem
              </label>
              <span className="text-xs text-slate-400">
                {message.length}/{MAX_MESSAGE}
              </span>
            </div>
            <textarea
              id="announcement-message"
              rows={6}
              className={`${fieldClass} resize-y`}
              placeholder="Escreva o comunicado que os usuários verão no app…"
              value={message}
              maxLength={MAX_MESSAGE}
              onChange={(event) => setMessage(event.target.value)}
              required
            />
          </div>

          <AttachmentPicker
            files={files}
            onChange={(next) => {
              setComposerError(null);
              setFiles(next);
            }}
            disabled={isBusy}
          />

          {composerError ? (
            <div
              ref={composerErrorRef}
              role="alert"
              className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"
            >
              <HiInformationCircle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>{composerError}</span>
            </div>
          ) : null}

          {!canSubmit && !isBusy && blockReason ? (
            <p className="text-xs text-slate-500">{blockReason}</p>
          ) : null}

          <div className="flex gap-2">
            <Button
              type="submit"
              className="flex-1"
              disabled={!canSubmit}
              isProcessing={isBusy}
            >
              {sendLabel}
            </Button>
            <Button color="light" onClick={() => setIsComposerOpen(false)}>
              Cancelar
            </Button>
          </div>
        </form>
      </Modal>
    </>
  );
};

export default AnnouncementsTable;
